import React, { useEffect } from 'react';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { Box, xcss, Inline, Stack } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { FilterFunction, Person } from '../common/types';
import { useStandupStoreActions, StandupStoreContainer } from '../controllers/store';
import { StandupTimerStoreContainer } from '../controllers/timer-store';
import messages from './messages';
import { ParticipantList } from './participant';
import Timer from './timer';

type StandupPanelProps = {
	assignees: Person[];
	setAssigneeFilter: FilterFunction;
	currentlyFilteredAssignee: string | null;
};

export const StandupPanel = ({
	assignees,
	setAssigneeFilter,
	currentlyFilteredAssignee,
}: StandupPanelProps) => {
	return (
		<StandupStoreContainer>
			<StandupTimerStoreContainer>
				<StandupPanelInternal
					assignees={assignees}
					setAssigneeFilter={setAssigneeFilter}
					currentlyFilteredAssignee={currentlyFilteredAssignee}
				/>
			</StandupTimerStoreContainer>
		</StandupStoreContainer>
	);
};

const StandupPanelInternal = ({
	assignees,
	setAssigneeFilter,
	currentlyFilteredAssignee,
}: StandupPanelProps) => {
	const { formatMessage } = useIntl();

	const {
		setParticipants,
		setFilterFunction,
		setFilteredParticipant,
		shuffleParticipants,
		toggleActiveStandup,
	} = useStandupStoreActions();

	useEffect(() => {
		setFilterFunction(setAssigneeFilter);
		// We want to clear any set assignee on first load
		setAssigneeFilter(null);
	}, [setAssigneeFilter, setFilterFunction]);

	useEffect(() => {
		setParticipants(assignees);
		// We want to clear any set assignee on first load
		shuffleParticipants();
	}, [assignees, setParticipants, shuffleParticipants]);

	useEffect(() => {
		setFilteredParticipant(currentlyFilteredAssignee || null);
	}, [currentlyFilteredAssignee, setFilteredParticipant]);

	return (
		<Box xcss={wrapperStyles} testId="standups.ui.wrapper">
			<Stack xcss={componentStyles} space="space.150">
				<Inline xcss={inlineSettingHeaderStyles} spread="space-between">
					<Heading size="xsmall">{formatMessage(messages.standupNonfinal)}</Heading>
					<IconButton
						onClick={() => toggleActiveStandup()}
						label={formatMessage(messages.closeLabelNonFinal)}
						icon={CrossIcon}
						appearance="subtle"
					/>
				</Inline>
				<Timer />
				<ParticipantList />
			</Stack>
		</Box>
	);
};

const componentStyles = xcss({
	color: 'color.text',
	width: '272px',
	height: '100%',
	background: token('elevation.surface'),
	borderRadius: 'border.radius.300',
	border: `1px solid ${token('color.border')}`,
});

const wrapperStyles = xcss({
	height: '100%',
	paddingBottom: 'space.300',
	paddingLeft: 'space.300',
	marginRight: 'space.negative.300',
	zIndex: 'card',
});

const inlineSettingHeaderStyles = xcss({
	alignItems: 'center',
	paddingInline: 'space.150',
	paddingTop: 'space.100',
});
